/**
 * Altheia brand mark — The Aperture.
 *
 * Six-bladed mechanical iris closing toward / opening from a hexagonal void.
 * The hex shares DNA with the product Hallmark glyph, so brand mark and
 * product chrome are one visual system, not two.
 *
 * Mitigations against "camera app" ambiguity baked in:
 *   1. Hex (not circle) outer frame — geometric, not optical
 *   2. Solo mark only via <MarkApertureFavicon/>; outbound surfaces use
 *      <WordmarkAperture/> to remove interpretation
 *   4. Inner hex void is transparent — what's "uncovered" is the page itself
 *      (Heideggerian unconcealment), not a backlit image
 *
 * Companion mitigations that live elsewhere (do not undo):
 *   3. Any motion is slow + paired with witness/inscription captions
 *   5. Surrounding copy never uses "watch/see/view/lens" — only "inscribe/
 *      witness/disclose/uncover"
 *   6. Crypto vocabulary (merkle, session-key, Swig, anchor) sits next to
 *      the mark in product chrome
 */

interface MarkProps {
  size?: number;
  color?: string;
  /** 0 = blades meet at center (concealed), 1 = fully retracted (revealed) */
  openAmt?: number;
}

function hexPoint(cx: number, cy: number, r: number, i: number, off = 0): [number, number] {
  const ang = ((i * 60 - 90 + off) * Math.PI) / 180;
  return [cx + Math.cos(ang) * r, cy + Math.sin(ang) * r];
}

export function MarkAperture({ size = 120, color = "var(--bone)", openAmt = 1 }: MarkProps) {
  const cx = 50;
  const cy = 50;
  const rOuter = 42;

  const outerHex = Array.from({ length: 6 }, (_, i) => hexPoint(cx, cy, rOuter, i))
    .map((p) => p.join(","))
    .join(" ");

  // Blades retract toward the rim as openAmt → 1.
  // closed (openAmt=0): blade tip at center (d=0). open: tip ~halfway out.
  const dInner = openAmt * (rOuter * 0.5);
  const blades: string[] = [];
  for (let i = 0; i < 6; i++) {
    const [x1, y1] = hexPoint(cx, cy, rOuter, i);
    const [x2, y2] = hexPoint(cx, cy, rOuter, (i + 1) % 6);
    const midAng = ((i * 60 - 90 + 30) * Math.PI) / 180;
    const x3 = cx + Math.cos(midAng) * dInner;
    const y3 = cy + Math.sin(midAng) * dInner;
    blades.push(`M ${x1} ${y1} L ${x2} ${y2} L ${x3} ${y3} Z`);
  }

  // Inner hex void — only render once the iris has opened enough to show one.
  const innerHexPath =
    openAmt > 0.05
      ? Array.from({ length: 6 }, (_, i) => hexPoint(cx, cy, rOuter * 0.5 * openAmt, i, 30))
          .map((p, i) => `${i === 0 ? "M" : "L"} ${p[0]} ${p[1]}`)
          .join(" ") + " Z"
      : null;

  return (
    <svg width={size} height={size} viewBox="0 0 100 100" role="img" aria-label="Altheia">
      <polygon points={outerHex} fill="none" stroke={color} strokeWidth="2.4" strokeLinejoin="miter" />
      {blades.map((d, i) => (
        <path key={i} d={d} fill={color} stroke={color} strokeWidth="0.8" strokeLinejoin="miter" />
      ))}
      {innerHexPath && (
        // Void: transparent fill — what's revealed is the page underneath, not an image.
        <path d={innerHexPath} fill="transparent" stroke={color} strokeWidth="1.0" strokeLinejoin="miter" />
      )}
    </svg>
  );
}

/**
 * Favicon-grade simplified mark — solid hex with a tiny inner hex void.
 * Survives 16px without losing the geometric tell.
 */
export function MarkApertureFavicon({ size = 16, color = "var(--bone)", voidColor = "var(--bg)" }: { size?: number; color?: string; voidColor?: string }) {
  const cx = 8;
  const cy = 8;
  const rO = 7;
  const rI = 2.4;
  const hex = (r: number) =>
    Array.from({ length: 6 }, (_, i) => {
      const a = ((i * 60 - 90) * Math.PI) / 180;
      return `${(cx + Math.cos(a) * r).toFixed(2)},${(cy + Math.sin(a) * r).toFixed(2)}`;
    }).join(" ");
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" role="img" aria-label="Altheia">
      <polygon points={hex(rO)} fill={color} />
      <polygon points={hex(rI)} fill={voidColor} />
    </svg>
  );
}

/* ============================================================
   DIRECTION 02 — THE CUT THETA
   ------------------------------------------------------------
   Greek Θ with horizontal bar replaced by a hairline slot that
   punches past the letterform. What was inside the closed glyph
   is now continuous with outside. Editorial / FT-Bloomberg.
   ============================================================ */
export function MarkCutTheta({ size = 120, color = "var(--bone)", accent = "var(--oxblood)" }: { size?: number; color?: string; accent?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" role="img" aria-label="Altheia">
      <ellipse cx="50" cy="50" rx="32" ry="40" fill="none" stroke={color} strokeWidth="3.4" />
      <line x1="6" y1="50" x2="94" y2="50" stroke={color} strokeWidth="2.4" />
      <line x1="91" y1="50" x2="94" y2="50" stroke={accent} strokeWidth="2.4" />
      <line x1="46" y1="10" x2="54" y2="10" stroke={color} strokeWidth="1.0" />
      <line x1="46" y1="90" x2="54" y2="90" stroke={color} strokeWidth="1.0" />
    </svg>
  );
}
export function MarkCutThetaFavicon({ size = 16, color = "var(--bone)" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" role="img" aria-label="Altheia">
      <ellipse cx="8" cy="8" rx="4.6" ry="6" fill="none" stroke={color} strokeWidth="1.2" />
      <line x1="0.5" y1="8" x2="15.5" y2="8" stroke={color} strokeWidth="1.0" />
    </svg>
  );
}

/* ============================================================
   DIRECTION 03 — THE LIFTED SEAL
   ------------------------------------------------------------
   Bronze disk with capital Α, hairline ghost outline showing
   where the seal was lifted from. The act of lifting is the
   mark. Charming, but reads heritage-society.
   ============================================================ */
export function MarkLiftedSeal({ size = 120, color = "var(--bone)", accent = "var(--bronze)", lifted = true }: { size?: number; color?: string; accent?: string; lifted?: boolean }) {
  const cx = 50;
  const cy = 50;
  const r = 32;
  const baseY = cy + 12;
  const apexY = cy - 14;
  const halfW = 11;
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" role="img" aria-label="Altheia">
      {lifted && (
        <g opacity="0.6">
          <circle cx={cx + 6} cy={cy + 4} r={r} fill="none" stroke={color} strokeWidth="0.6" strokeDasharray="2 2" />
        </g>
      )}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth="2.6" />
      <circle cx={cx} cy={cy} r={r - 4} fill="none" stroke={color} strokeWidth="0.6" />
      <line x1={cx - 7} y1={apexY - 7} x2={cx + 7} y2={apexY - 7} stroke={color} strokeWidth="1.4" />
      <line x1={cx - halfW} y1={baseY} x2={cx} y2={apexY} stroke={color} strokeWidth="3.0" />
      <line x1={cx + halfW} y1={baseY} x2={cx} y2={apexY} stroke={color} strokeWidth="3.0" />
      <line x1={cx - 6} y1={baseY - 8} x2={cx + 6} y2={baseY - 8} stroke={color} strokeWidth="1.4" />
      {lifted && <circle cx={cx + 22} cy={cy - 22} r="2.0" fill={accent} />}
    </svg>
  );
}
export function MarkLiftedSealFavicon({ size = 16, color = "var(--bone)" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" role="img" aria-label="Altheia">
      <circle cx="8" cy="8" r="6.4" fill="none" stroke={color} strokeWidth="1.2" />
      <line x1="4.6" y1="11.5" x2="8" y2="3.8" stroke={color} strokeWidth="1.3" />
      <line x1="11.4" y1="11.5" x2="8" y2="3.8" stroke={color} strokeWidth="1.3" />
      <line x1="6.0" y1="9" x2="10.0" y2="9" stroke={color} strokeWidth="0.9" />
    </svg>
  );
}

/* ============================================================
   DIRECTION 04 — THE LIGATURE (wordmark-led)
   ------------------------------------------------------------
   The brand mark IS the wordmark. Custom "Altheia" with a
   load-bearing "th" ligature — the t's crossbar fuses with the
   h's shoulder into a single glyph that's the brand seal at
   small sizes. Mercury / Mirror / Sky / Ethena pattern.
   ============================================================ */
export function MarkLigature({ size = 120, color = "var(--bone)" }: { size?: number; color?: string }) {
  // Just the "th" ligature lifted out as a glyph. Custom paths approximating
  // a Cormorant Garamond th with the crossbar of t extending into h's stem.
  return (
    <svg width={size} height={size * 0.86} viewBox="0 0 100 86" role="img" aria-label="Altheia">
      {/* t — vertical stem */}
      <line x1="22" y1="12" x2="22" y2="74" stroke={color} strokeWidth="6.2" strokeLinecap="butt" />
      {/* shared crossbar — the load-bearing connector across t into h */}
      <line x1="6" y1="26" x2="78" y2="26" stroke={color} strokeWidth="3.4" />
      {/* h — left vertical stem (continues from crossbar) */}
      <line x1="50" y1="26" x2="50" y2="74" stroke={color} strokeWidth="6.2" strokeLinecap="butt" />
      {/* h — right vertical stem */}
      <line x1="78" y1="40" x2="78" y2="74" stroke={color} strokeWidth="6.2" strokeLinecap="butt" />
      {/* h — bowl curve (top of right stem) */}
      <path d="M 50 40 Q 64 32 78 40" fill="none" stroke={color} strokeWidth="6.2" strokeLinejoin="miter" />
      {/* serifs */}
      <line x1="14" y1="74" x2="30" y2="74" stroke={color} strokeWidth="2.6" />
      <line x1="42" y1="74" x2="58" y2="74" stroke={color} strokeWidth="2.6" />
      <line x1="70" y1="74" x2="86" y2="74" stroke={color} strokeWidth="2.6" />
      {/* t terminal */}
      <line x1="22" y1="12" x2="22" y2="14" stroke={color} strokeWidth="2.4" />
      {/* tiny accent tick on crossbar — institutional punctuation */}
      <line x1="74" y1="26" x2="78" y2="26" stroke="var(--bronze)" strokeWidth="3.4" />
    </svg>
  );
}
export function MarkLigatureFavicon({ size = 16, color = "var(--bone)" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" role="img" aria-label="Altheia">
      <line x1="3" y1="2" x2="3" y2="13" stroke={color} strokeWidth="1.4" />
      <line x1="0.5" y1="4.5" x2="12" y2="4.5" stroke={color} strokeWidth="1.0" />
      <line x1="7.5" y1="4.5" x2="7.5" y2="13" stroke={color} strokeWidth="1.4" />
      <line x1="12" y1="7" x2="12" y2="13" stroke={color} strokeWidth="1.4" />
      <path d="M 7.5 7 Q 9.7 5.5 12 7" fill="none" stroke={color} strokeWidth="1.2" />
    </svg>
  );
}

/* ============================================================
   DIRECTION 05 — THE WITNESS LINE
   ------------------------------------------------------------
   Greek capital Α whose crossbar extends out as a long
   horizontal rule — a witness signature line on a contract.
   Α anchors the line; the line is the act of inscription.
   Reads scribe / archivist, not photographer / surveiller.
   ============================================================ */
export function MarkWitnessLine({ size = 120, color = "var(--bone)", accent = "var(--bronze)" }: { size?: number; color?: string; accent?: string }) {
  return (
    <svg width={size} height={size * 0.55} viewBox="0 0 180 100" role="img" aria-label="Altheia">
      {/* Greek Α — Trajan-ish, thick + thin contrast */}
      <line x1="20" y1="86" x2="50" y2="14" stroke={color} strokeWidth="6" strokeLinecap="butt" />
      <line x1="80" y1="86" x2="50" y2="14" stroke={color} strokeWidth="9" strokeLinecap="butt" />
      {/* crossbar — extends FAR past the Α into the witness line */}
      <line x1="32" y1="60" x2="172" y2="60" stroke={color} strokeWidth="2.4" />
      {/* terminus accent tick — bronze institutional pip */}
      <line x1="166" y1="60" x2="172" y2="60" stroke={accent} strokeWidth="2.4" />
      {/* base serifs on the Α */}
      <line x1="14" y1="86" x2="26" y2="86" stroke={color} strokeWidth="2.0" />
      <line x1="74" y1="86" x2="86" y2="86" stroke={color} strokeWidth="2.0" />
      {/* apex tick */}
      <line x1="46" y1="14" x2="54" y2="14" stroke={color} strokeWidth="1.4" />
    </svg>
  );
}
export function MarkWitnessLineFavicon({ size = 16, color = "var(--bone)" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" role="img" aria-label="Altheia">
      <line x1="3" y1="14" x2="8" y2="2" stroke={color} strokeWidth="1.4" />
      <line x1="13" y1="14" x2="8" y2="2" stroke={color} strokeWidth="1.6" />
      <line x1="5" y1="9.5" x2="15.5" y2="9.5" stroke={color} strokeWidth="1.0" />
    </svg>
  );
}

/* ============================================================
   DIRECTION 06 — THE MANUSCRIPT INITIAL
   ------------------------------------------------------------
   Greek Α as an illuminated drop cap inside a square frame —
   the kind that opens a chapter in a medieval codex. Lives
   in the Greek/illuminated tradition the brand already runs.
   Zero crypto overlap.
   ============================================================ */
export function MarkManuscriptInitial({ size = 120, color = "var(--bone)", accent = "var(--bronze)" }: { size?: number; color?: string; accent?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" role="img" aria-label="Altheia">
      {/* Outer frame */}
      <rect x="6" y="6" width="88" height="88" fill="none" stroke={color} strokeWidth="2.0" />
      {/* Inner double-rule (illumination convention) */}
      <rect x="11" y="11" width="78" height="78" fill="none" stroke={color} strokeWidth="0.6" />
      {/* Corner accents — minimal, restraint over Victorian */}
      <line x1="6" y1="14" x2="14" y2="14" stroke={accent} strokeWidth="1.4" />
      <line x1="14" y1="6" x2="14" y2="14" stroke={accent} strokeWidth="1.4" />
      <line x1="86" y1="6" x2="86" y2="14" stroke={accent} strokeWidth="1.4" />
      <line x1="86" y1="14" x2="94" y2="14" stroke={accent} strokeWidth="1.4" />
      <line x1="6" y1="86" x2="14" y2="86" stroke={accent} strokeWidth="1.4" />
      <line x1="14" y1="86" x2="14" y2="94" stroke={accent} strokeWidth="1.4" />
      <line x1="86" y1="86" x2="86" y2="94" stroke={accent} strokeWidth="1.4" />
      <line x1="86" y1="86" x2="94" y2="86" stroke={accent} strokeWidth="1.4" />
      {/* Greek capital Α — illuminated, Trajan-influenced */}
      <line x1="32" y1="78" x2="50" y2="22" stroke={color} strokeWidth="6.6" strokeLinecap="butt" />
      <line x1="68" y1="78" x2="50" y2="22" stroke={color} strokeWidth="6.6" strokeLinecap="butt" />
      <line x1="40" y1="58" x2="60" y2="58" stroke={color} strokeWidth="2.6" />
      {/* base serifs */}
      <line x1="26" y1="78" x2="38" y2="78" stroke={color} strokeWidth="2.0" />
      <line x1="62" y1="78" x2="74" y2="78" stroke={color} strokeWidth="2.0" />
      {/* apex tick */}
      <line x1="46" y1="22" x2="54" y2="22" stroke={color} strokeWidth="1.4" />
    </svg>
  );
}
export function MarkManuscriptInitialFavicon({ size = 16, color = "var(--bone)" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" role="img" aria-label="Altheia">
      <rect x="0.5" y="0.5" width="15" height="15" fill="none" stroke={color} strokeWidth="1.0" />
      <line x1="5" y1="13" x2="8" y2="3" stroke={color} strokeWidth="1.4" />
      <line x1="11" y1="13" x2="8" y2="3" stroke={color} strokeWidth="1.4" />
      <line x1="6.2" y1="9.6" x2="9.8" y2="9.6" stroke={color} strokeWidth="0.9" />
    </svg>
  );
}

/* ============================================================
   DIRECTION 07 — THE PRIVATIVE Α
   ------------------------------------------------------------
   The brand mark is just the capital alpha. But the crossbar
   is the load-bearing element — emphasized in stroke + bronze
   tick — because in Greek grammar, the α-privative is the
   negation prefix that turns λήθη (lethe / forgetting) into
   ἀ-λήθεια (aletheia / un-forgetting / truth). The bar across
   the alpha is what does the work. Modernist, etymology-led.
   ============================================================ */
export function MarkPrivativeAlpha({ size = 120, color = "var(--bone)", accent = "var(--bronze)" }: { size?: number; color?: string; accent?: string }) {
  // Α with crossbar emphasized as the brand device. Trajan-ish proportions.
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" role="img" aria-label="Altheia · Privative Α">
      {/* Α — left stroke (thinner), right stroke (thicker — Trajan contrast) */}
      <line x1="22" y1="84" x2="50" y2="16" stroke={color} strokeWidth="5.4" strokeLinecap="butt" />
      <line x1="78" y1="84" x2="50" y2="16" stroke={color} strokeWidth="8.4" strokeLinecap="butt" />
      {/* The crossbar — the privative — load-bearing, extends slightly past both strokes */}
      <line x1="32" y1="58" x2="68" y2="58" stroke={color} strokeWidth="3.8" />
      {/* Bronze terminus tick — the privative as inscription */}
      <line x1="65" y1="58" x2="68" y2="58" stroke={accent} strokeWidth="3.8" />
      {/* Base serifs — institutional anchor */}
      <line x1="14" y1="84" x2="30" y2="84" stroke={color} strokeWidth="2.0" />
      <line x1="70" y1="84" x2="86" y2="84" stroke={color} strokeWidth="2.0" />
      {/* Apex tick */}
      <line x1="46" y1="16" x2="54" y2="16" stroke={color} strokeWidth="1.4" />
    </svg>
  );
}
export function MarkPrivativeAlphaFavicon({ size = 16, color = "var(--bone)", accent = "var(--bronze)" }: { size?: number; color?: string; accent?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" role="img" aria-label="Altheia">
      <line x1="3.4" y1="13.5" x2="8" y2="2.5" stroke={color} strokeWidth="1.4" />
      <line x1="12.6" y1="13.5" x2="8" y2="2.5" stroke={color} strokeWidth="1.8" />
      <line x1="5.2" y1="9.4" x2="10.8" y2="9.4" stroke={color} strokeWidth="1.0" />
      <line x1="10.4" y1="9.4" x2="10.8" y2="9.4" stroke={accent} strokeWidth="1.0" />
    </svg>
  );
}

/* ============================================================
   DIRECTION 08 — Αλ BOUND
   ------------------------------------------------------------
   Capital Α and lowercase λ as a fused ligature. The α holds
   (and negates) the λ inside its frame. Visually shows the
   privative containing the lethe — alpha-privative literally
   negating the forgetting it precedes in the word. Two letters,
   one glyph.
   ============================================================ */
export function MarkAlphaLambda({ size = 120, color = "var(--bone)", accent = "var(--bronze)" }: { size?: number; color?: string; accent?: string }) {
  return (
    <svg width={size} height={size * 0.86} viewBox="0 0 100 86" role="img" aria-label="Altheia · Αλ ligature">
      {/* Α — the dominant letterform */}
      <line x1="14" y1="74" x2="38" y2="14" stroke={color} strokeWidth="5.4" />
      <line x1="62" y1="74" x2="38" y2="14" stroke={color} strokeWidth="8.4" />
      {/* Α crossbar — the privative — at ~58% height */}
      <line x1="22" y1="50" x2="54" y2="50" stroke={color} strokeWidth="3.4" />
      {/* λ — emerges from inside the Α's right side, sharing the bottom-right zone */}
      {/* λ apex at the Α's crossbar terminus, extending diagonally down-right past the Α */}
      <line x1="54" y1="50" x2="86" y2="74" stroke={color} strokeWidth="5.4" />
      {/* λ short upper stroke — kicks back from the apex up-left */}
      <line x1="54" y1="50" x2="48" y2="40" stroke={color} strokeWidth="3.4" />
      {/* Bronze tick at λ's terminus — the inscription */}
      <line x1="82" y1="71.4" x2="86" y2="74" stroke={accent} strokeWidth="5.4" />
      {/* Α base serifs */}
      <line x1="8" y1="74" x2="20" y2="74" stroke={color} strokeWidth="2.0" />
      <line x1="56" y1="74" x2="68" y2="74" stroke={color} strokeWidth="2.0" />
    </svg>
  );
}
export function MarkAlphaLambdaFavicon({ size = 16, color = "var(--bone)" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" role="img" aria-label="Altheia">
      <line x1="2" y1="13" x2="6.5" y2="2" stroke={color} strokeWidth="1.4" />
      <line x1="11" y1="13" x2="6.5" y2="2" stroke={color} strokeWidth="1.6" />
      <line x1="3.6" y1="9" x2="9.4" y2="9" stroke={color} strokeWidth="1.0" />
      <line x1="9.4" y1="9" x2="14" y2="13" stroke={color} strokeWidth="1.4" />
    </svg>
  );
}

/* ============================================================
   DIRECTION 09 — THE WITNESS Α
   ------------------------------------------------------------
   Capital Α whose crossbar (the privative) extends out
   horizontally as a long ledger rule. Two metaphors collapsed
   into one form: the privative alpha that produces truth, and
   the inscription line on which witnessed events are recorded.
   The Α anchors the rule; the rule is the act of inscription.
   ============================================================ */
export function MarkWitnessAlpha({ size = 120, color = "var(--bone)", accent = "var(--bronze)" }: { size?: number; color?: string; accent?: string }) {
  return (
    <svg width={size * 1.6} height={size} viewBox="0 0 160 100" role="img" aria-label="Altheia · Witness Α">
      {/* Α — slightly compressed to leave room for the rule */}
      <line x1="14" y1="84" x2="38" y2="16" stroke={color} strokeWidth="5.4" />
      <line x1="62" y1="84" x2="38" y2="16" stroke={color} strokeWidth="8.4" />
      {/* The crossbar — extends FAR past the Α as a witness rule */}
      <line x1="22" y1="58" x2="152" y2="58" stroke={color} strokeWidth="2.4" />
      {/* Bronze terminus tick — inscription complete */}
      <line x1="146" y1="58" x2="152" y2="58" stroke={accent} strokeWidth="2.4" />
      {/* Base serifs */}
      <line x1="8" y1="84" x2="20" y2="84" stroke={color} strokeWidth="2.0" />
      <line x1="56" y1="84" x2="68" y2="84" stroke={color} strokeWidth="2.0" />
      {/* Apex tick */}
      <line x1="34" y1="16" x2="42" y2="16" stroke={color} strokeWidth="1.4" />
    </svg>
  );
}
export function MarkWitnessAlphaFavicon({ size = 16, color = "var(--bone)" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" role="img" aria-label="Altheia">
      <line x1="2" y1="13" x2="5.5" y2="3" stroke={color} strokeWidth="1.4" />
      <line x1="9" y1="13" x2="5.5" y2="3" stroke={color} strokeWidth="1.6" />
      <line x1="3.5" y1="9" x2="15.5" y2="9" stroke={color} strokeWidth="1.0" />
    </svg>
  );
}

/**
 * Brand glyph — the serif "A" lifted out of the wordmark.
 * Same geometry as /favicon.svg so brand mark and tab icon are one system.
 */
export function GlyphA({ size = 22, color = "var(--bone)" }: { size?: number; color?: string }) {
  // 32x32 viewBox matching /favicon.svg geometry, sized to caller.
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" role="img" aria-label="Altheia">
      <line x1="7" y1="26" x2="14.4" y2="6" stroke={color} strokeWidth="2.6" strokeLinecap="butt" />
      <line x1="25" y1="26" x2="14.4" y2="6" stroke={color} strokeWidth="3.6" strokeLinecap="butt" />
      <line x1="9.5" y1="19.4" x2="22.5" y2="19.4" stroke={color} strokeWidth="1.6" />
      <line x1="4" y1="26" x2="11" y2="26" stroke={color} strokeWidth="1.4" />
      <line x1="21" y1="26" x2="28" y2="26" stroke={color} strokeWidth="1.4" />
    </svg>
  );
}

/**
 * SHIPPING wordmark — pure type, no glyph.
 * Wordmark-led identity (Mercury / Mirror / Ethena pattern).
 */
export function WordmarkAltheia({
  size = 22,
  color = "var(--bone)",
}: {
  size?: number;
  color?: string;
}) {
  return (
    <span
      style={{
        fontFamily: "'Cormorant Garamond', 'Instrument Serif', 'Times New Roman', serif",
        fontSize: size,
        fontWeight: 500,
        color,
        letterSpacing: "-0.005em",
        lineHeight: 1,
        display: "inline-block",
      }}
    >
      Altheia
    </span>
  );
}

/**
 * Aperture wordmark — kept for /brand exploration and historical reference.
 * Not used in shipping chrome.
 */
export function WordmarkAperture({
  height = 28,
  color = "var(--bone)",
  showSubtitle = false,
}: {
  height?: number;
  color?: string;
  showSubtitle?: boolean;
}) {
  const markSize = height * 1.0;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: height * 0.32 }}>
      <MarkAperture size={markSize} color={color} openAmt={1} />
      <span style={{ display: "inline-flex", flexDirection: "column", gap: 2, lineHeight: 1 }}>
        <span
          style={{
            fontFamily: "'Cormorant Garamond', 'Instrument Serif', 'Times New Roman', serif",
            fontSize: height * 0.94,
            fontWeight: 500,
            color,
            letterSpacing: "-0.005em",
          }}
        >
          Altheia
        </span>
        {showSubtitle && (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: height * 0.18,
              letterSpacing: "0.24em",
              textTransform: "uppercase",
              color: "var(--slate)",
              marginTop: 4,
            }}
          >
            The witness for AI agents
          </span>
        )}
      </span>
    </span>
  );
}
